/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Formik } from "formik";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import {
  POLICY_ROUTE,
} from "../../constants/routes.js";
import { usePolicyService } from "services/policyService.js";
import {
  createValidationPolicyYupSchema,
  useValidationService,
} from "../../resources/validations/validations.js";
import { useGlobalContext } from "contexts/globalContext.js";
import { useGlobalUpdateContext } from "contexts/globalContext.js";
import { useUserContext } from "contexts/userContext";
import { ACCEPTED_LANGUAGES } from '../../constants/appConstants';
import moment from 'helpers/moment';

/* Components */
import UCButton from "../../components/buttons/index.js";
import PolicySettingsCard from "./policySettingsCard/index.js";
import Breadcrumbs from "../../components/breadcrumbs/index.js";
import EditPolicyModal from "../../components/modals/editPolicyModal/index.js";
import PolicySkeleton from "./skeleton/policySkeleton.js";
import PolicyUnsavedChangesModal from "../../components/modals/policyUnsavedChangesModal/index.js";

/* Styles */
import styles from "./styles.js";
import { useLocaleContext } from "../../contexts/localeContext.js";

const EditHotelPolicy = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const userDetails = useUserContext();

  const globalProperties = useGlobalContext();
  const setGlobalProperties = useGlobalUpdateContext();
  const { state } = useLocation();
  const formRef = useRef(null);
  const [showEditPolicyModal, setShowEditPolicyModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const { getHotelPolicyDetails, editPolicyDetails } = usePolicyService();
  const { editHotelPolicyMockedValidationObj } = useValidationService();
  const [policyResponse, setPolicyResponse] = useState({});
  const [hotelPolicySchema, setHotelPolicySchema] = useState({});
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [proceedAfterSubmit, setProceedAfterSubmit] = useState(false);
  const globalLocale = useLocaleContext();

  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const isChoice = providerCode === "CHOICE";

  const { policyId: policyIdFromParams } = useParams();
  const policyId = state?.policyId || policyIdFromParams;

  useEffect(() => {
    const result = createValidationPolicyYupSchema(editHotelPolicyMockedValidationObj);
    setHotelPolicySchema(result);
  }, []);

  useEffect(() => {
    (async () => {
      const res = await getHotelPolicyDetails(policyId);
      if (res) setPolicyResponse(res);
    })();
  }, [policyId]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formRef.current?.dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    setGlobalProperties({
      ...globalProperties,
      addFirstPolicyData: {
        ...globalProperties.addFirstPolicyData,
        policyName: state?.policyName,
        policyId: state?.policyId
      }
    })
  }, [])

  const handleSaveAndContinue = () => {
    setProceedAfterSubmit(true);
    formRef.current.handleSubmit();
  };

  const handleCancel = () => {
    setShowUnsavedChangesModal(false);
    setPendingNavigation(null);
  };

  const transformFormValuesToPolicy = (
    formValues,
    existingPolicyData
  ) => {
    // Build generalRule object
    const generalRule = {
      allowedBrands: formValues.allowableBrands?.map(brand => typeof brand === 'string' ? brand : brand.optionValue) || [],
      maxRate: formValues.maxPricePerNight ? String(formValues.maxPricePerNight) : "0",
      minimumStarRating: formValues.minimumStarRating || "any",
      currencyCode: formValues.currency || "USD",
    };

    if (existingPolicyData?.generalRule?.policyRuleId) {
      generalRule.policyRuleId = existingPolicyData.generalRule.policyRuleId;
    }

    const transformedData = {
      policyId: existingPolicyData?.policyId || state?.policyId,
      policyName: existingPolicyData?.policyName,
      policySettings: {
        allowNonRefundable: String(formValues.allowNonRefundableFares || false),
        refundableRatesRequired: String(formValues.requiredRefundableFares || false),
        advancePurchaseRequired: String(formValues.requiredAdvancePurchase || false),
        advancePurchase: formValues.advancePurchase ? String(formValues.advancePurchase) : "",
        policyRuleId: existingPolicyData?.policySettings?.policyRuleId || "",
      },
      generalRule,
    };

    return transformedData;
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const submitForm = async (values, setSubmitting) => {
    const data = transformFormValuesToPolicy(values, policyResponse);
    const success = await editPolicyDetails(data, state?.policyId, globalProperties.firstTimeSaved);
    setSubmitting(false);

    if (success) {
      if (proceedAfterSubmit && pendingNavigation) {
        pendingNavigation();
        setProceedAfterSubmit(false);
        setPendingNavigation(null);
      } else {
        navigate("../" + POLICY_ROUTE);
      }
    }
  };

  const LastUpdatedMessage = ({ updatedBy, updatedOn }) => {
    const updatedDate = updatedOn ? moment(updatedOn).toDate() : null;
    const formattedDate = updatedDate.toLocaleDateString(ACCEPTED_LANGUAGES[globalLocale], {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
    const formattedTime = updatedDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    return (
      <FormattedMessage
        id="editPolicy.lastUpdatedText"
        values={{
          date: formattedDate,
          time: formattedTime,
          name: updatedBy,
        }}
      />
    );
  };

  return (
    <div>
      <div className={classes.pageHeader}>
        <Breadcrumbs
          items={[
            <Link to={POLICY_ROUTE}>
              <FormattedMessage id="breadcrumbs.policy" />
            </Link>,
            policyResponse?.policyName,
          ]}
        />
        <Container className={classes.container}>
          <Row>
            <Col xs={12} sm={8} className="gx-0">
              <h1 className={classes.title}>{policyResponse?.policyName}</h1>
              <h3 className={classes.subtitle}>
                <FormattedMessage id="editPolicy.subtitle" />
              </h3>
            </Col>
            <Col xs={12} sm={4} className={classes.btnWrapper}>
              <UCButton
                type="default"
                className={classes.policyBtn}
                onClick={() => setShowEditPolicyModal(true)}
                text={<FormattedMessage id="editPolicy.editNameBtn" />}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <hr className={classes.divider} />
      {Object.keys(policyResponse).length > 0 ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            maxPricePerNight:
              policyResponse?.generalRule?.maxRate || "0",
            currency:
              policyResponse?.generalRule?.currencyCode || "USD",
            allowableBrands:
              policyResponse?.generalRule?.allowedBrands || [],
            allowNonRefundableFares:
              policyResponse?.policySettings?.allowNonRefundable === "true" || policyResponse?.policySettings?.allowNonRefundable === true,
            requiredRefundableFares:
              policyResponse?.policySettings?.refundableRatesRequired === "true" || policyResponse?.policySettings?.refundableRatesRequired === true,
            requiredAdvancePurchase:
              policyResponse?.policySettings?.advancePurchaseRequired === "true" || policyResponse?.policySettings?.advancePurchaseRequired === true,
            advancePurchase: policyResponse?.policySettings?.advancePurchase?.toString() || (policyResponse?.policySettings?.advancePurchaseRequired ? "1" : null),
            minimumStarRating:
              policyResponse?.generalRule?.minimumStarRating?.toString() || "3",
          }}
          validationSchema={hotelPolicySchema}
          onSubmit={(values, { setSubmitting }) => {
            submitForm(values, setSubmitting);
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <>
              <form onSubmit={handleSubmit}>
                <PolicySettingsCard
                  values={values}
                  handleFormSubmit={handleFormSubmit}
                  setFieldValue={setFieldValue}
                  formRef={formRef}
                  showSaveBtn={state?.showSaveBtn}
                  policyResponse={policyResponse}
                />
                <div className={classes.policyFooter}>
                  {policyResponse?.updatedOn && (
                    <LastUpdatedMessage
                      updatedBy={policyResponse?.updatedBy}
                      updatedOn={policyResponse?.updatedOn}
                    />
                  )}
                </div>
              </form>
            </>
          )}
        </Formik>
      ) : (
        <PolicySkeleton />
      )}
      <EditPolicyModal
        showModal={showEditPolicyModal}
        setShowModal={setShowEditPolicyModal}
        policyName={policyResponse?.policyName}
        policyId={state?.policyId}
        setPolicyResponse={setPolicyResponse}
      />
      <PolicyUnsavedChangesModal
        showModal={showUnsavedChangesModal}
        setShowModal={setShowUnsavedChangesModal}
        onSave={handleSaveAndContinue}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default EditHotelPolicy;
